import type React from 'react';
import { isBefore, isSameDay } from 'date-fns';
import { coerceToDate } from '@common/paymentsUtils/dateUtils';
import { Anchor, Box, Icon, Paragraph, Text } from '@zillow/constellation-10';
import {
  IconCheckmarkCircleFilled,
  IconClockFilled,
  IconExclamationCircleFilled,
  IconMinusCircleFilled,
} from '@zillow/constellation-icons';
import type { PaymentReportHistoryInterface } from '@renter-hub/models/Types';
import TransactionConstants from '@common/constants/TransactionConstants';
import type { RenterTransactionProps } from '@renter-hub/models/Types';
import { QuestionMarkIconToolTip } from './QuestionMarkIconToolTip';

interface PaymentReportingStatusProps {
  transaction: RenterTransactionProps;
  paymentReportHistory?: PaymentReportHistoryInterface;
  currentScheduleMonth?: {
    scheduleMonth: string;
    dueDate: string;
  };
  showVisibleText?: boolean;
}

export const PaymentReportingStatus: React.FC<React.PropsWithChildren<PaymentReportingStatusProps>> = (props) => {
  const { paymentReportHistory, transaction, showVisibleText = false } = props;
  const { renterStatus, scheduleMonth, paymentCategory } = transaction;
  const { optInDate, paymentReportMonths } = paymentReportHistory || {};
  const paymentReportMonthsSet = new Set(paymentReportMonths);
  const notSentToolTipContent = (
    <>
      <Text>
        Payments are not reported to credit bureaus if you miss a payment, make a late payment, opt-out of the service,
        or if your landlord stops accepting Zillow payments. This will not impact your credit score.{' '}
        <Anchor
          href="https://zillow.zendesk.com/hc/en-us/articles/360057505314-Where-do-I-go-to-make-my-rent-payment"
          target="_blank"
        >
          Learn more
        </Anchor>
      </Text>
    </>
  );
  const sentToolTipContent = <Text>Includes payments made before you enrolled in rent reporting.</Text>;
  const paymentDueToolTipContent = (
    <Text>
      You have not made a rent payment for this month yet. If a payment is made this month, it could still be reported
      as on-time to the major credit bureaus.
    </Text>
  );
  const pendingToolTipContent = <Text>Your rent payment was on-time and will be reported next month.</Text>;
  const iconMinus = (
    <Icon size="md" css={{ color: 'icon.subtle', marginRight: 'xs' }} render={<IconMinusCircleFilled />} />
  );
  const iconCheckmark = (
    <Icon
      size="md"
      css={{ color: 'icon.feedback.success.strong', marginRight: 'xs' }}
      render={<IconCheckmarkCircleFilled />}
    />
  );
  const iconWarning = (
    <Icon
      size="md"
      css={{ color: 'icon.feedback.warning.strong', marginRight: 'xs' }}
      render={<IconExclamationCircleFilled />}
    />
  );
  const iconSending = (
    <Icon size="md" css={{ color: 'icon.accent.blue.strong', marginRight: 'xs' }} render={<IconClockFilled />} />
  );

  let icon = iconMinus;
  let status;
  let toolTipContent;

  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth() + 1;
  const formattedDate = `${year}/${month.toString().padStart(2, '0')}`;
  const todayMonth = coerceToDate(formattedDate);
  const scheduleMonthDate = coerceToDate(scheduleMonth);

  if (!!todayMonth && !!scheduleMonthDate && isSameDay(todayMonth, scheduleMonthDate)) {
    if (renterStatus === TransactionConstants.STATUS.MANUAL || renterStatus === TransactionConstants.STATUS.COMPLETE) {
      icon = iconSending;
      status = 'Sending';
      toolTipContent = pendingToolTipContent;
    } else {
      icon = iconWarning;
      status = 'Payment due';
      toolTipContent = paymentDueToolTipContent;
    }
  } else if (paymentReportMonthsSet.has(transaction.scheduleMonth)) {
    icon = iconCheckmark;
    status = 'Sent';
    if (optInDate && isBefore(coerceToDate(scheduleMonth) as Date, coerceToDate(optInDate) as Date)) {
      toolTipContent = sentToolTipContent;
    }
  } else if (paymentCategory === TransactionConstants.CATEGORY.RENT_PAYMENT) {
    status = 'Not sent';
    toolTipContent = notSentToolTipContent;
  } else {
    return <Text>Not applicable</Text>;
  }

  const statusContent = (
    <>
      <Text css={{ fontWeight: showVisibleText ? 'bold' : 'normal' }}>{status}</Text>
      {toolTipContent && showVisibleText ? (
        <Text css={{ fontSize: '14px', fontWeight: 'normal', display: 'inline' }}> - {toolTipContent}</Text>
      ) : (
        toolTipContent && <QuestionMarkIconToolTip content={<Paragraph>{toolTipContent}</Paragraph>} />
      )}
    </>
  );

  return (
    <Box css={{ display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
      <Box css={{ display: 'flex', alignSelf: 'center' }}>{icon}</Box>
      <Box>{statusContent}</Box>
    </Box>
  );
};
